import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Package,
  Settings,
  ShoppingCart,
  User,
  Wrench,
  XCircle,
} from 'lucide-react'
import { useAuth } from '@/auth/AuthProvider'
import { getApiErrorMessage } from '@/api/client'
import { useEngineers } from '@/api/engineers'
import {
  useAcceptWorkOrder,
  useAcceptanceFail,
  useAcceptancePass,
  useBindDeviceAsset,
  useCancelWorkOrder,
  useCheckParts,
  useCloseExceptionWorkOrder,
  useCompleteDiagnosis,
  useCompleteRepair,
  useDispatchWorkOrder,
  usePrepareParts,
  useRedispatchWorkOrder,
  useRejectWorkOrder,
  useReopenWorkOrder,
  useReportMissingParts,
  useRequestRediagnosis,
  useStartDiagnosis,
  useStartRepair,
  useWorkOrderDetail,
  useWorkOrderTimeline,
} from '@/api/work-order'
import {
  ACTION_TYPE_LABEL,
  URGENCY_COLOR,
  URGENCY_LABEL,
  WORK_ORDER_STATUS_COLOR,
  WORK_ORDER_STATUS_LABEL,
  type DispatchRequest,
  type PartItemRequest,
  type UrgencyLevel,
  type WorkOrder,
  type WorkOrderStatus,
} from '@/api/types/work-order'
import { InlineError } from '@/components/ErrorDisplay'
import { Loading } from '@/components/Loading'
import { useToast } from '@/components/ToastProvider'
import { useDeviceAssets } from '@/hooks/asset'
import { useInventoryItems } from '@/hooks/inventory'

type TabKey = 'overview' | 'dispatch' | 'diagnosis' | 'repair' | 'acceptance'
type ModalType =
  | 'reject'
  | 'cancel'
  | 'close-exception'
  | 'reopen'
  | 'dispatch'
  | 'redispatch'
  | 'bind-device'
  | 'start-diagnosis'
  | 'complete-diagnosis'
  | 'prepare-parts'
  | 'check-parts'
  | 'start-repair'
  | 'complete-repair'
  | 'missing-parts'
  | 'rediagnosis'
  | 'acceptance-pass'
  | 'acceptance-fail'

interface ActionButton {
  key: string
  label: string
  modal?: ModalType
  primary?: boolean
  onClick?: () => Promise<void>
}

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: '基本信息' },
  { key: 'dispatch', label: '派单记录' },
  { key: 'diagnosis', label: '诊断记录' },
  { key: 'repair', label: '维修记录' },
  { key: 'acceptance', label: '验收记录' },
]

const COLOR_CLASS: Record<string, { text: string; bg: string }> = {
  default: { text: 'text-gray-700', bg: 'bg-gray-100' },
  processing: { text: 'text-blue-700', bg: 'bg-blue-100' },
  blue: { text: 'text-blue-700', bg: 'bg-blue-100' },
  orange: { text: 'text-orange-700', bg: 'bg-orange-100' },
  gold: { text: 'text-yellow-700', bg: 'bg-yellow-100' },
  purple: { text: 'text-purple-700', bg: 'bg-purple-100' },
  cyan: { text: 'text-cyan-700', bg: 'bg-cyan-100' },
  geekblue: { text: 'text-indigo-700', bg: 'bg-indigo-100' },
  success: { text: 'text-green-700', bg: 'bg-green-100' },
  warning: { text: 'text-amber-700', bg: 'bg-amber-100' },
  error: { text: 'text-red-700', bg: 'bg-red-100' },
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('zh-CN') : '-'
}

function formatBool(value?: boolean | null) {
  if (value == null) return '-'
  return value ? '是' : '否'
}

function StatusBadge({ status }: { status: WorkOrderStatus }) {
  const color = WORK_ORDER_STATUS_COLOR[status] ?? 'default'
  const cls = COLOR_CLASS[color] ?? COLOR_CLASS.default
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${cls.text} ${cls.bg}`}>
      {WORK_ORDER_STATUS_LABEL[status] ?? status}
    </span>
  )
}

function UrgencyBadge({ level }: { level?: UrgencyLevel }) {
  if (!level) return null
  const color = URGENCY_COLOR[level] ?? 'default'
  const cls = COLOR_CLASS[color] ?? COLOR_CLASS.default
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${cls.text} ${cls.bg}`}>
      {URGENCY_LABEL[level] ?? level}
    </span>
  )
}

function SectionCard({
  title,
  icon,
  children,
  extra,
}: {
  title: string
  icon?: ReactNode
  children: ReactNode
  extra?: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
          {icon}
          <span>{title}</span>
        </div>
        {extra}
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-1 text-sm text-gray-900">{value ?? '-'}</div>
    </div>
  )
}

function EmptyBlock({ text }: { text: string }) {
  return <div className="rounded-xl bg-gray-50 px-4 py-8 text-center text-sm text-gray-400">{text}</div>
}

function ModalShell({
  title,
  description,
  children,
  onClose,
}: {
  title: string
  description?: string
  children: ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
            </div>
            <button onClick={onClose} className="text-sm text-gray-400 transition hover:text-gray-600">
              关闭
            </button>
          </div>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

function ModalActions({
  onClose,
  submitting,
  submitText,
}: {
  onClose: () => void
  submitting: boolean
  submitText: string
}) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-600 transition hover:bg-gray-50"
      >
        取消
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="rounded-xl bg-primary-500 px-4 py-2 text-sm text-white transition hover:bg-primary-600 disabled:opacity-50"
      >
        {submitting ? '提交中...' : submitText}
      </button>
    </div>
  )
}

function ReasonModal({
  title,
  description,
  submitText,
  placeholder,
  onSubmit,
  onClose,
}: {
  title: string
  description?: string
  submitText: string
  placeholder: string
  onSubmit: (reason: string) => Promise<void>
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(reason.trim())
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title={title} description={description} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={4}
          required
          placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <ModalActions onClose={onClose} submitting={submitting} submitText={submitText} />
      </form>
    </ModalShell>
  )
}

function TextModal({
  title,
  description,
  placeholder,
  submitText,
  defaultValue = '',
  onSubmit,
  onClose,
}: {
  title: string
  description?: string
  placeholder: string
  submitText: string
  defaultValue?: string
  onSubmit: (value: string) => Promise<void>
  onClose: () => void
}) {
  const [value, setValue] = useState(defaultValue)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(value.trim())
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title={title} description={description} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={4}
          required
          placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <ModalActions onClose={onClose} submitting={submitting} submitText={submitText} />
      </form>
    </ModalShell>
  )
}

function DispatchModal({
  title,
  onSubmit,
  onClose,
}: {
  title: string
  onSubmit: (payload: DispatchRequest) => Promise<void>
  onClose: () => void
}) {
  const { data: engineers, isLoading } = useEngineers()
  const [engineerId, setEngineerId] = useState('')
  const [remark, setRemark] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const selectedEngineer = engineers?.find((item) => item.userId === engineerId)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!selectedEngineer) return
    setSubmitting(true)
    try {
      await onSubmit({
        engineerId: selectedEngineer.userId,
        engineerName: selectedEngineer.userName,
        remark: remark.trim() || undefined,
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title={title} description="从真实工程师列表中选择派单对象" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">工程师</label>
          <select
            value={engineerId}
            onChange={(event) => setEngineerId(event.target.value)}
            required
            disabled={isLoading}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{isLoading ? '加载工程师中...' : '请选择工程师'}</option>
            {engineers?.map((engineer) => (
              <option key={engineer.userId} value={engineer.userId}>
                {engineer.userName} ({engineer.userId})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">备注</label>
          <textarea
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            rows={3}
            placeholder="可填写派单说明或现场信息"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <ModalActions onClose={onClose} submitting={submitting} submitText="确认派单" />
      </form>
    </ModalShell>
  )
}

function DeviceBindModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (deviceAssetId: number) => Promise<void>
  onClose: () => void
}) {
  const { data: devices, isLoading } = useDeviceAssets()
  const [deviceAssetId, setDeviceAssetId] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(Number(deviceAssetId))
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="绑定设备资产" description="选择设备档案并回写到工单快照" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <select
          value={deviceAssetId}
          onChange={(event) => setDeviceAssetId(event.target.value)}
          required
          disabled={isLoading}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">{isLoading ? '加载设备中...' : '请选择设备资产'}</option>
          {devices?.map((device) => (
            <option key={device.id} value={device.id}>
              {device.assetCode} / {device.deviceName} / {device.hospitalName}
            </option>
          ))}
        </select>
        <ModalActions onClose={onClose} submitting={submitting} submitText="确认绑定" />
      </form>
    </ModalShell>
  )
}

function StartDiagnosisModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (mode: 'REMOTE' | 'ONSITE') => Promise<void>
  onClose: () => void
}) {
  const [mode, setMode] = useState<'REMOTE' | 'ONSITE'>('ONSITE')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(mode)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="开始诊断" description="选择本次诊断方式" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <select
          value={mode}
          onChange={(event) => setMode(event.target.value as 'REMOTE' | 'ONSITE')}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="ONSITE">现场诊断</option>
          <option value="REMOTE">远程诊断</option>
        </select>
        <ModalActions onClose={onClose} submitting={submitting} submitText="开始诊断" />
      </form>
    </ModalShell>
  )
}

function CompleteDiagnosisModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (payload: {
    problemClear: boolean
    needContinueVisit: boolean
    needParts: boolean
    faultCategory?: string
    suggestion?: string
    summary: string
  }) => Promise<void>
  onClose: () => void
}) {
  const [problemClear, setProblemClear] = useState(true)
  const [needContinueVisit, setNeedContinueVisit] = useState(false)
  const [needParts, setNeedParts] = useState(false)
  const [faultCategory, setFaultCategory] = useState('')
  const [suggestion, setSuggestion] = useState('')
  const [summary, setSummary] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({
        problemClear,
        needContinueVisit,
        needParts,
        faultCategory: faultCategory.trim() || undefined,
        suggestion: suggestion.trim() || undefined,
        summary: summary.trim(),
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="完成诊断" description="填写故障结论和后续处理建议" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm">
            <input type="checkbox" checked={problemClear} onChange={(event) => setProblemClear(event.target.checked)} />
            问题已明确
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm">
            <input
              type="checkbox"
              checked={needContinueVisit}
              onChange={(event) => setNeedContinueVisit(event.target.checked)}
            />
            需要继续上门
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm">
            <input type="checkbox" checked={needParts} onChange={(event) => setNeedParts(event.target.checked)} />
            需要配件
          </label>
        </div>
        <input
          value={faultCategory}
          onChange={(event) => setFaultCategory(event.target.value)}
          placeholder="故障分类，例如电源、球管、线圈"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <textarea
          value={suggestion}
          onChange={(event) => setSuggestion(event.target.value)}
          rows={2}
          placeholder="处理建议，可选"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          rows={4}
          required
          placeholder="诊断结论和现场说明"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <ModalActions onClose={onClose} submitting={submitting} submitText="提交诊断结果" />
      </form>
    </ModalShell>
  )
}

function PartsModal({
  title,
  description,
  withSource,
  onSubmit,
  onClose,
}: {
  title: string
  description?: string
  withSource?: boolean
  onSubmit: (payload: { source?: string; parts: PartItemRequest[] }) => Promise<void>
  onClose: () => void
}) {
  const { data: inventoryItems } = useInventoryItems()
  const [source, setSource] = useState('INVENTORY')
  const [selectedSkuCode, setSelectedSkuCode] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [parts, setParts] = useState<PartItemRequest[]>([])
  const [submitting, setSubmitting] = useState(false)

  const handleAddPart = () => {
    const inventory = inventoryItems?.find((item) => item.skuCode === selectedSkuCode)
    if (!inventory) return

    setParts((current) => {
      const exists = current.find((item) => item.skuCode === inventory.skuCode)
      if (exists) {
        return current.map((item) =>
          item.skuCode === inventory.skuCode ? { ...item, quantity } : item
        )
      }
      return [
        ...current,
        {
          skuCode: inventory.skuCode,
          partName: inventory.partName,
          quantity,
          critical: true,
        },
      ]
    })

    setSelectedSkuCode('')
    setQuantity(1)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({
        source: withSource ? source.trim() || 'INVENTORY' : undefined,
        parts,
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title={title} description={description} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {withSource ? (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">来源</label>
            <select
              value={source}
              onChange={(event) => setSource(event.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="INVENTORY">INVENTORY</option>
              <option value="PROCUREMENT">PROCUREMENT</option>
              <option value="MANUAL">MANUAL</option>
            </select>
          </div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-[1fr_112px_96px]">
          <select
            value={selectedSkuCode}
            onChange={(event) => setSelectedSkuCode(event.target.value)}
            className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">选择配件</option>
            {inventoryItems?.map((item) => (
              <option key={item.skuCode} value={item.skuCode}>
                {item.partName} ({item.skuCode}) / 可用 {item.availableQty}
              </option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(event) => setQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))}
            className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={handleAddPart}
            className="rounded-xl bg-primary-50 px-4 py-3 text-sm font-medium text-primary-700 transition hover:bg-primary-100"
          >
            添加
          </button>
        </div>
        <div className="space-y-2">
          {parts.map((part, index) => (
            <div key={part.skuCode} className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <div className="text-sm text-gray-700">
                {part.partName} ({part.skuCode}) x {part.quantity}
              </div>
              <button
                type="button"
                onClick={() => setParts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                className="text-sm text-red-500 transition hover:text-red-600"
              >
                删除
              </button>
            </div>
          ))}
          {parts.length === 0 ? <EmptyBlock text="请先添加至少一个配件" /> : null}
        </div>
        <ModalActions onClose={onClose} submitting={submitting} submitText="确认提交" />
      </form>
    </ModalShell>
  )
}

function CompleteRepairModal({
  onSubmit,
  onClose,
}: {
  onSubmit: (payload: { summary: string; selfCheckPassed: boolean }) => Promise<void>
  onClose: () => void
}) {
  const [summary, setSummary] = useState('')
  const [selfCheckPassed, setSelfCheckPassed] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({ summary: summary.trim(), selfCheckPassed })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="完成维修" description="提交维修总结并确认自检通过" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          rows={4}
          required
          placeholder="填写维修过程和结果"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm">
          <input
            type="checkbox"
            checked={selfCheckPassed}
            onChange={(event) => setSelfCheckPassed(event.target.checked)}
          />
          自检通过，可以进入验收
        </label>
        <ModalActions onClose={onClose} submitting={submitting} submitText="完成维修" />
      </form>
    </ModalShell>
  )
}

function AcceptanceModal({
  title,
  submitText,
  onSubmit,
  onClose,
}: {
  title: string
  submitText: string
  onSubmit: (remark: string) => Promise<void>
  onClose: () => void
}) {
  const [remark, setRemark] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(remark.trim())
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title={title} description="验收意见会沉淀到工单验收记录中" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <textarea
          value={remark}
          onChange={(event) => setRemark(event.target.value)}
          rows={4}
          required
          placeholder="填写验收说明"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <ModalActions onClose={onClose} submitting={submitting} submitText={submitText} />
      </form>
    </ModalShell>
  )
}

function WorkOrderOverview({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <div className="space-y-4">
      <SectionCard title="客户信息" icon={<Building2 className="h-4 w-4 text-gray-400" />}>
        <div className="grid gap-4 md:grid-cols-3">
          <InfoRow label="客户名称" value={workOrder.customerName} />
          <InfoRow label="医院名称" value={workOrder.hospitalName} />
          <InfoRow label="联系人" value={workOrder.contactName} />
          <InfoRow label="联系电话" value={workOrder.contactPhone} />
          <InfoRow label="来源类型" value={workOrder.sourceType} />
          <InfoRow label="客户类型" value={workOrder.customerType} />
        </div>
      </SectionCard>

      <SectionCard title="设备信息" icon={<Settings className="h-4 w-4 text-gray-400" />}>
        <div className="grid gap-4 md:grid-cols-3">
          <InfoRow label="设备名称" value={workOrder.deviceName} />
          <InfoRow label="设备型号" value={workOrder.deviceModel} />
          <InfoRow label="序列号" value={workOrder.deviceSerialNo} />
          <InfoRow label="安装位置" value={workOrder.installLocation} />
          <InfoRow label="科室" value={workOrder.department} />
          <InfoRow label="资产编码" value={workOrder.deviceAsset?.assetCode} />
        </div>
      </SectionCard>

      <SectionCard title="故障描述" icon={<AlertTriangle className="h-4 w-4 text-gray-400" />}>
        <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{workOrder.faultDesc || '暂无故障描述'}</p>
      </SectionCard>

      <SectionCard title="配件需求" icon={<Package className="h-4 w-4 text-gray-400" />}>
        {workOrder.partRequirements?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">配件</th>
                  <th className="pb-3 font-medium">需求</th>
                  <th className="pb-3 font-medium">已预留</th>
                  <th className="pb-3 font-medium">已消耗</th>
                  <th className="pb-3 font-medium">来源</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {workOrder.partRequirements.map((part) => (
                  <tr key={part.id} className="border-t border-gray-100">
                    <td className="py-3">
                      <div className="font-medium text-gray-900">{part.partName}</div>
                      <div className="text-xs text-gray-400">{part.skuCode}</div>
                    </td>
                    <td className="py-3 text-gray-600">{part.requiredQty}</td>
                    <td className="py-3 text-gray-600">{part.reservedQty}</td>
                    <td className="py-3 text-gray-600">{part.consumedQty}</td>
                    <td className="py-3 text-gray-600">{part.source || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyBlock text="当前没有配件需求记录" />
        )}
      </SectionCard>

      <SectionCard title="采购单" icon={<ShoppingCart className="h-4 w-4 text-gray-400" />}>
        {workOrder.purchaseOrders?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">采购单号</th>
                  <th className="pb-3 font-medium">供应商</th>
                  <th className="pb-3 font-medium">预计交付</th>
                  <th className="pb-3 font-medium">仓库</th>
                  <th className="pb-3 font-medium">状态</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {workOrder.purchaseOrders.map((order) => (
                  <tr key={order.id} className="border-t border-gray-100">
                    <td className="py-3 font-medium text-primary-600">
                      <Link to={`/supply-chain/purchase/${order.id}`}>{order.purchaseNo}</Link>
                    </td>
                    <td className="py-3 text-gray-600">{order.supplierName}</td>
                    <td className="py-3 text-gray-600">{formatDate(order.expectedDeliveryDate)}</td>
                    <td className="py-3 text-gray-600">{order.warehouseCode || '-'}</td>
                    <td className="py-3 text-gray-600">{order.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyBlock text="当前没有关联采购单" />
        )}
      </SectionCard>

      <SectionCard title="报价单">
        {workOrder.quotations?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">版本</th>
                  <th className="pb-3 font-medium">报价单号</th>
                  <th className="pb-3 font-medium">金额</th>
                  <th className="pb-3 font-medium">销售</th>
                  <th className="pb-3 font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {workOrder.quotations.map((quotation) => (
                  <tr key={quotation.id} className="border-t border-gray-100">
                    <td className="py-3 text-gray-600">V{quotation.versionNo}</td>
                    <td className="py-3 font-medium text-gray-900">{quotation.quoteNo || '-'}</td>
                    <td className="py-3 text-gray-600">{quotation.totalAmount}</td>
                    <td className="py-3 text-gray-600">{quotation.salesName || '-'}</td>
                    <td className="py-3 text-gray-600">{formatDate(quotation.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyBlock text="当前没有报价记录" />
        )}
      </SectionCard>
    </div>
  )
}

export default function WorkOrderDetail() {
  const { id } = useParams()
  const workOrderId = Number(id)
  const { session } = useAuth()
  const toast = useToast()
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [modalType, setModalType] = useState<ModalType | null>(null)

  const detailQuery = useWorkOrderDetail(workOrderId)
  const timelineQuery = useWorkOrderTimeline(workOrderId)

  const acceptMutation = useAcceptWorkOrder()
  const rejectMutation = useRejectWorkOrder()
  const cancelMutation = useCancelWorkOrder()
  const closeExceptionMutation = useCloseExceptionWorkOrder()
  const reopenMutation = useReopenWorkOrder()
  const dispatchMutation = useDispatchWorkOrder()
  const redispatchMutation = useRedispatchWorkOrder()
  const bindDeviceMutation = useBindDeviceAsset()
  const startDiagnosisMutation = useStartDiagnosis()
  const completeDiagnosisMutation = useCompleteDiagnosis()
  const preparePartsMutation = usePrepareParts()
  const checkPartsMutation = useCheckParts()
  const startRepairMutation = useStartRepair()
  const completeRepairMutation = useCompleteRepair()
  const missingPartsMutation = useReportMissingParts()
  const rediagnosisMutation = useRequestRediagnosis()
  const acceptancePassMutation = useAcceptancePass()
  const acceptanceFailMutation = useAcceptanceFail()

  const handleSuccess = (message: string) => {
    toast.success(message)
    setModalType(null)
  }

  const handleError = (error: unknown) => {
    toast.error(getApiErrorMessage(error))
  }

  const workOrder = detailQuery.data
  const timeline = timelineQuery.data ?? []
  const activeDispatch = useMemo(() => {
    if (!workOrder?.dispatches?.length) return undefined
    return workOrder.dispatches.find((item) => item.currentActive) ?? workOrder.dispatches[workOrder.dispatches.length - 1]
  }, [workOrder?.dispatches])

  const role = session?.role
  const isManager = role === 'ADMIN' || role === 'STATION_MANAGER'
  const isProcurement = role === 'ADMIN' || role === 'PROCUREMENT' || role === 'STATION_MANAGER'
  const isAcceptanceRole = role === 'ADMIN' || role === 'STATION_MANAGER' || role === 'SALES'
  const isAssignedEngineer = role === 'ADMIN' || (role === 'ENGINEER' && activeDispatch?.engineerId === session?.userId)
  const terminalStatuses: WorkOrderStatus[] = ['REJECTED', 'CANCELLED', 'CLOSED_EXCEPTION', 'COMPLETED']

  if (!Number.isFinite(workOrderId)) {
    return (
      <div className="p-6">
        <InlineError message="工单编号无效" />
      </div>
    )
  }

  if (detailQuery.isLoading || timelineQuery.isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载工单详情..." />
      </div>
    )
  }

  if (detailQuery.error || !workOrder) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(detailQuery.error)} />
      </div>
    )
  }

  const actionButtons: ActionButton[] = []

  if (isManager && workOrder.status === 'CREATED') {
    actionButtons.push(
      {
        key: 'accept',
        label: '接收工单',
        primary: true,
        onClick: async () => {
          await acceptMutation.mutateAsync(workOrderId)
          handleSuccess('工单已接收')
        },
      },
      {
        key: 'reject',
        label: '拒绝工单',
        modal: 'reject',
      }
    )
  }

  if (isManager && (workOrder.status === 'ACCEPTED' || workOrder.status === 'REOPENED')) {
    actionButtons.push({ key: 'dispatch', label: '派单', modal: 'dispatch', primary: true })
  }

  if (
    isManager &&
    activeDispatch &&
    !['CREATED', 'ACCEPTED', 'REJECTED', 'CANCELLED', 'CLOSED_EXCEPTION', 'COMPLETED'].includes(workOrder.status)
  ) {
    actionButtons.push({ key: 'redispatch', label: '改派', modal: 'redispatch' })
  }

  if (isManager && ['CREATED', 'ACCEPTED', 'DISPATCHED', 'WAITING_CONFIRM', 'WAITING_PARTS'].includes(workOrder.status)) {
    actionButtons.push({ key: 'cancel', label: '取消工单', modal: 'cancel' })
  }

  if (isManager && !terminalStatuses.includes(workOrder.status)) {
    actionButtons.push({ key: 'close-exception', label: '异常关闭', modal: 'close-exception' })
  }

  if (isManager && workOrder.status === 'COMPLETED') {
    actionButtons.push({ key: 'reopen', label: '重开工单', modal: 'reopen' })
  }

  if (isManager) {
    actionButtons.push({ key: 'bind-device', label: workOrder.deviceAsset ? '更换绑定设备' : '绑定设备', modal: 'bind-device' })
  }

  if (isAssignedEngineer && workOrder.status === 'DISPATCHED') {
    actionButtons.push({ key: 'start-diagnosis', label: '开始诊断', modal: 'start-diagnosis', primary: true })
  }

  if (isAssignedEngineer && workOrder.status === 'DIAGNOSING') {
    actionButtons.push({ key: 'complete-diagnosis', label: '完成诊断', modal: 'complete-diagnosis', primary: true })
  }

  if (isProcurement && workOrder.status === 'WAITING_PARTS') {
    actionButtons.push(
      { key: 'prepare-parts', label: '备件准备', modal: 'prepare-parts', primary: true },
      { key: 'check-parts', label: '检查齐套', modal: 'check-parts' }
    )
  }

  if (isAssignedEngineer && workOrder.status === 'READY_FOR_REPAIR') {
    actionButtons.push({ key: 'start-repair', label: '开始维修', modal: 'start-repair', primary: true })
  }

  if (isAssignedEngineer && workOrder.status === 'IN_REPAIR') {
    actionButtons.push(
      { key: 'complete-repair', label: '完成维修', modal: 'complete-repair', primary: true },
      { key: 'missing-parts', label: '上报缺件', modal: 'missing-parts' },
      { key: 'rediagnosis', label: '重新诊断', modal: 'rediagnosis' }
    )
  }

  if (isAcceptanceRole && workOrder.status === 'WAITING_ACCEPTANCE') {
    actionButtons.push(
      { key: 'acceptance-pass', label: '验收通过', modal: 'acceptance-pass', primary: true },
      { key: 'acceptance-fail', label: '验收失败', modal: 'acceptance-fail' }
    )
  }

  const renderModal = () => {
    switch (modalType) {
      case 'reject':
        return (
          <ReasonModal
            title="拒绝工单"
            description="拒绝原因会写入工单日志，替换掉原来的固定占位文案。"
            submitText="确认拒绝"
            placeholder="请输入拒绝原因"
            onSubmit={async (reason) => {
              await rejectMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('工单已拒绝')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'cancel':
        return (
          <ReasonModal
            title="取消工单"
            submitText="确认取消"
            placeholder="请输入取消原因"
            onSubmit={async (reason) => {
              await cancelMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('工单已取消')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'close-exception':
        return (
          <ReasonModal
            title="异常关闭"
            submitText="确认关闭"
            placeholder="请输入异常关闭原因"
            onSubmit={async (reason) => {
              await closeExceptionMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('工单已异常关闭')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'reopen':
        return (
          <ReasonModal
            title="重开工单"
            submitText="确认重开"
            placeholder="请输入重开原因"
            onSubmit={async (reason) => {
              await reopenMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('工单已重开')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'dispatch':
        return (
          <DispatchModal
            title="派单"
            onSubmit={async (data) => {
              await dispatchMutation.mutateAsync({ id: workOrderId, data })
              handleSuccess('派单成功')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'redispatch':
        return (
          <DispatchModal
            title="改派"
            onSubmit={async (data) => {
              await redispatchMutation.mutateAsync({ id: workOrderId, data })
              handleSuccess('改派成功')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'bind-device':
        return (
          <DeviceBindModal
            onSubmit={async (deviceAssetId) => {
              await bindDeviceMutation.mutateAsync({ id: workOrderId, data: { deviceAssetId } })
              handleSuccess('设备绑定成功')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'start-diagnosis':
        return (
          <StartDiagnosisModal
            onSubmit={async (mode) => {
              await startDiagnosisMutation.mutateAsync({ id: workOrderId, data: { mode } })
              handleSuccess('已开始诊断')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'complete-diagnosis':
        return (
          <CompleteDiagnosisModal
            onSubmit={async (data) => {
              await completeDiagnosisMutation.mutateAsync({ id: workOrderId, data })
              handleSuccess('诊断已完成')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'prepare-parts':
        return (
          <PartsModal
            title="备件准备"
            description="把实际准备好的配件同步到工单，并尝试自动进入待维修。"
            withSource
            onSubmit={async ({ source, parts }) => {
              await preparePartsMutation.mutateAsync({
                id: workOrderId,
                data: {
                  source: source || 'INVENTORY',
                  parts,
                },
              })
              handleSuccess('备件准备已提交')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'check-parts':
        return (
          <TextModal
            title="检查备件齐套"
            description="后端会重新尝试预留库存，并在条件满足时切到待维修。"
            placeholder="请输入检查说明"
            submitText="确认检查"
            defaultValue="库存已核对，尝试转入待维修"
            onSubmit={async (reason) => {
              await checkPartsMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('已执行备件检查')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'start-repair':
        return (
          <TextModal
            title="开始维修"
            placeholder="请输入维修计划或现场说明"
            submitText="开始维修"
            onSubmit={async (summary) => {
              await startRepairMutation.mutateAsync({ id: workOrderId, data: { summary } })
              handleSuccess('已开始维修')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'complete-repair':
        return (
          <CompleteRepairModal
            onSubmit={async (data) => {
              await completeRepairMutation.mutateAsync({ id: workOrderId, data })
              handleSuccess('维修已完成')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'missing-parts':
        return (
          <PartsModal
            title="上报缺件"
            description="维修缺件会把工单重新推回待配件状态。"
            onSubmit={async ({ parts }) => {
              await missingPartsMutation.mutateAsync({ id: workOrderId, parts })
              handleSuccess('缺件已上报')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'rediagnosis':
        return (
          <TextModal
            title="重新诊断"
            placeholder="请输入重新诊断原因"
            submitText="提交申请"
            onSubmit={async (reason) => {
              await rediagnosisMutation.mutateAsync({ id: workOrderId, reason })
              handleSuccess('已发起重新诊断')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'acceptance-pass':
        return (
          <AcceptanceModal
            title="验收通过"
            submitText="确认通过"
            onSubmit={async (remark) => {
              await acceptancePassMutation.mutateAsync({ id: workOrderId, data: { remark } })
              handleSuccess('验收已通过')
            }}
            onClose={() => setModalType(null)}
          />
        )
      case 'acceptance-fail':
        return (
          <AcceptanceModal
            title="验收失败"
            submitText="确认失败"
            onSubmit={async (remark) => {
              await acceptanceFailMutation.mutateAsync({ id: workOrderId, data: { remark } })
              handleSuccess('已记录验收失败')
            }}
            onClose={() => setModalType(null)}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="p-6">
      <Link to="/work-orders" className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" />
        返回工单列表
      </Link>

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <h1 className="font-mono text-2xl font-semibold text-gray-900">{workOrder.orderNo}</h1>
              <StatusBadge status={workOrder.status} />
              <UrgencyBadge level={workOrder.urgencyLevel} />
            </div>
            <p className="text-sm text-gray-600">{workOrder.title || '未填写工单标题'}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-400">创建时间</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{formatDate(workOrder.createdAt)}</div>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-400">最后更新时间</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{formatDate(workOrder.updatedAt)}</div>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-400">当前工程师</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{activeDispatch?.engineerName || '-'}</div>
              </div>
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-400">重开次数</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{workOrder.reopenCount ?? 0}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {actionButtons.map((button) => (
              <button
                key={button.key}
                onClick={() => {
                  if (button.modal) {
                    setModalType(button.modal)
                    return
                  }
                  button.onClick?.().catch(handleError)
                }}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                  button.primary
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' ? <WorkOrderOverview workOrder={workOrder} /> : null}

          {activeTab === 'dispatch' ? (
            <SectionCard
              title="派单记录"
              icon={<User className="h-4 w-4 text-gray-400" />}
              extra={<span className="text-xs text-gray-400">{workOrder.dispatches?.length ?? 0} 条记录</span>}
            >
              {workOrder.dispatches?.length ? (
                <div className="space-y-4">
                  {workOrder.dispatches.map((dispatch) => (
                    <div key={dispatch.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-gray-900">{dispatch.engineerName}</span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{dispatch.status}</span>
                            {dispatch.currentActive ? (
                              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">当前负责</span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">{dispatch.engineerId}</div>
                          {dispatch.remark ? <p className="mt-2 text-sm text-gray-700">{dispatch.remark}</p> : null}
                        </div>
                        <div className="text-xs text-gray-400">{formatDate(dispatch.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyBlock text="暂无派单记录" />
              )}
            </SectionCard>
          ) : null}

          {activeTab === 'diagnosis' ? (
            <SectionCard
              title="诊断记录"
              icon={<AlertTriangle className="h-4 w-4 text-gray-400" />}
              extra={<span className="text-xs text-gray-400">{workOrder.diagnoses?.length ?? 0} 条记录</span>}
            >
              {workOrder.diagnoses?.length ? (
                <div className="space-y-4">
                  {workOrder.diagnoses.map((diagnosis) => (
                    <div key={diagnosis.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">第 {diagnosis.roundNo} 次诊断</span>
                        <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-xs text-cyan-700">
                          {diagnosis.mode === 'REMOTE' ? '远程' : '现场'}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{diagnosis.status}</span>
                        <span className="ml-auto text-xs text-gray-400">{formatDate(diagnosis.createdAt)}</span>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <InfoRow label="工程师" value={diagnosis.engineerName} />
                        <InfoRow label="故障分类" value={diagnosis.faultCategory} />
                        <InfoRow label="问题已明确" value={formatBool(diagnosis.problemClear)} />
                        <InfoRow label="需要配件" value={formatBool(diagnosis.needParts)} />
                        <InfoRow label="需要继续上门" value={formatBool(diagnosis.needContinueVisit)} />
                      </div>
                      {diagnosis.suggestion ? (
                        <div className="mt-3 text-sm text-gray-700">
                          <span className="font-medium text-gray-900">处理建议：</span>
                          {diagnosis.suggestion}
                        </div>
                      ) : null}
                      {diagnosis.summary ? (
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-700">{diagnosis.summary}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyBlock text="暂无诊断记录" />
              )}
            </SectionCard>
          ) : null}

          {activeTab === 'repair' ? (
            <SectionCard
              title="维修记录"
              icon={<Wrench className="h-4 w-4 text-gray-400" />}
              extra={<span className="text-xs text-gray-400">{workOrder.repairs?.length ?? 0} 条记录</span>}
            >
              {workOrder.repairs?.length ? (
                <div className="space-y-4">
                  {workOrder.repairs.map((repair) => (
                    <div key={repair.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">第 {repair.roundNo} 次维修</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{repair.status}</span>
                        <span className="ml-auto text-xs text-gray-400">{formatDate(repair.createdAt)}</span>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <InfoRow label="工程师" value={repair.engineerName} />
                        <InfoRow label="自检通过" value={formatBool(repair.selfCheckPassed)} />
                        <InfoRow label="自检结果" value={repair.selfCheckResult} />
                      </div>
                      {repair.summary ? (
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-700">{repair.summary}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyBlock text="暂无维修记录" />
              )}
            </SectionCard>
          ) : null}

          {activeTab === 'acceptance' ? (
            <SectionCard
              title="验收记录"
              icon={<CheckCircle2 className="h-4 w-4 text-gray-400" />}
              extra={<span className="text-xs text-gray-400">{workOrder.acceptances?.length ?? 0} 条记录</span>}
            >
              {workOrder.acceptances?.length ? (
                <div className="space-y-4">
                  {workOrder.acceptances.map((acceptance) => (
                    <div key={acceptance.id} className="rounded-xl border border-gray-100 p-4">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">第 {acceptance.roundNo} 次验收</span>
                        {acceptance.result === 'PASSED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                            <CheckCircle2 className="h-3 w-3" />
                            验收通过
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                            <XCircle className="h-3 w-3" />
                            验收失败
                          </span>
                        )}
                        <span className="ml-auto text-xs text-gray-400">{formatDate(acceptance.createdAt)}</span>
                      </div>
                      <InfoRow label="验收人" value={acceptance.acceptedByName} />
                      {acceptance.remark ? <p className="mt-3 text-sm text-gray-700">{acceptance.remark}</p> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyBlock text="暂无验收记录" />
              )}
            </SectionCard>
          ) : null}
        </div>

        <div className="min-w-0">
          <div className="sticky top-6 space-y-6">
            <SectionCard title="工单状态" icon={<Clock3 className="h-4 w-4 text-gray-400" />}>
              <div className="space-y-3">
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-400">当前状态</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">
                    {WORK_ORDER_STATUS_LABEL[workOrder.status] ?? workOrder.status}
                  </div>
                </div>
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-400">当前工程师</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{activeDispatch?.engineerName || '-'}</div>
                </div>
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-400">设备资产</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{workOrder.deviceAsset?.assetCode || '-'}</div>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="操作日志" icon={<Clock3 className="h-4 w-4 text-gray-400" />}>
              {timeline.length ? (
                <div className="relative space-y-5">
                  <div className="absolute bottom-2 left-[11px] top-2 w-px bg-gray-200" />
                  {[...timeline].reverse().map((log, index) => (
                    <div key={log.id} className="relative flex gap-3">
                      <div
                        className={`z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                          index === 0 ? 'bg-primary-500' : 'border-2 border-gray-300 bg-white'
                        }`}
                      >
                        {index === 0 ? <div className="h-2 w-2 rounded-full bg-white" /> : null}
                      </div>
                      <div className="min-w-0 flex-1 pb-1">
                        <div className="text-sm font-medium text-gray-900">
                          {ACTION_TYPE_LABEL[log.actionType] ?? log.actionType}
                        </div>
                        {log.fromStatus && log.toStatus ? (
                          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <span>{WORK_ORDER_STATUS_LABEL[log.fromStatus as WorkOrderStatus] ?? log.fromStatus}</span>
                            <ChevronRight className="h-3 w-3 text-gray-300" />
                            <span className="font-medium text-gray-700">
                              {WORK_ORDER_STATUS_LABEL[log.toStatus as WorkOrderStatus] ?? log.toStatus}
                            </span>
                          </div>
                        ) : null}
                        {log.operatorName ? (
                          <div className="mt-1 text-xs text-gray-500">
                            {log.operatorName}
                            {log.operatorRole ? ` / ${log.operatorRole}` : ''}
                          </div>
                        ) : null}
                        {log.remark ? <p className="mt-1 text-xs italic text-gray-500">"{log.remark}"</p> : null}
                        <div className="mt-1 text-xs text-gray-400">{formatDate(log.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyBlock text="暂无操作日志" />
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      {renderModal()}
    </div>
  )
}
